import Avo from 'analytics/Avo';
import type { Cards } from 'api/types/cards.types';
import { AddCardIcon, DeleteIcon } from 'assets/icons/';
import AddPaymentCardSheet from 'components/molecules/AddPaymentCardSheet';
import BottomSheet, { type BottomSheetProps } from 'components/molecules/BottomSheet';
import { useConfirmationDialog } from 'components/molecules/ConfirmationModal';
import { StyledFormCheck } from 'components/molecules/StyledFormCheck';
import { selectCurrentOrder } from 'pages/newOrder/newOrder.selectors';
import { setSelectedPaymentCard } from 'pages/newOrder/newOrder.slice';
import { confirmApplePayMethod } from 'pages/newOrder/newOrder.thunks';
import { useAddPaymentCard, useDeletePaymentCard, usePaymentCards, useUpdateDefaultPaymentMethod } from 'pages/queries/payment.queries';
import React, { forwardRef, useCallback, useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import ListGroup from 'react-bootstrap/ListGroup';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { hasApplePayPayment } from 'redux-stores/slices/authSlice';
import type { AppDispatch } from 'redux-stores/store';
import { checkApplyPayCompatibility } from 'wrappers/apple_pay';

import CardIcon from './card-icon';
import SelectPaymentMethodSheet from './selectPaymentMethodSheet';

interface CardItemProps {
  card: Cards.Card;
  isSelected: boolean;
  handleCardSelected(cardId: string): any;
  handleDeleteCard?(cardId: string): any;
}

const CardItem: React.FC<CardItemProps> = ({ handleCardSelected = () => {}, card, isSelected, handleDeleteCard }) => {
  const isApplePay = card.id === 'applePay';
  const { t } = useTranslation();

  const onClick = useCallback(() => handleDeleteCard?.(card.id), []);

  return (
    <ListGroup.Item className="bg-white py-3 px-0" key={card.id}>
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <CardIcon type={card.type} />
          <h4 className="mb-0 margin-start-2 text-dark">
            {isApplePay ? t('newOrderPage.applePay') : t('newOrderPage.cardHiddenNumber', { lastFourDigits: card.last4 })}
          </h4>
        </div>
        <div className="d-flex align-items-center">
          {!isSelected && !!handleDeleteCard && (
            <div className="btn p-0 margin-end-7" onClick={onClick} aria-hidden="true">
              <DeleteIcon />
            </div>
          )}
          <StyledFormCheck
            name="payment-card"
            defaultChecked={isSelected}
            title={card.type}
            custom
            type="radio"
            id={`card-${card.id}`}
            label=""
            value={card.id}
            onChange={() => handleCardSelected(card.id)}
          />
        </div>
      </div>
    </ListGroup.Item>
  );
};

interface Props extends BottomSheetProps {
  onCardSelected?(cardId: string): any;
}

export interface SelectPaymentSheetProps {
  toggleSelectPaymentMethodSheet(): any;
}

const SelectPaymentSheet = forwardRef<SelectPaymentSheetProps, Props>(({ ...props }, ref) => {
  const dispatch = useDispatch<AppDispatch>();

  const [isAddPaymentSheetOpen, setIsAddPaymentSheetOpen] = React.useState(false);
  const [isSelectPaymentOpen, setIsSelectPaymentOpen] = React.useState(false);
  const { getConfirmation } = useConfirmationDialog();
  const isApplePay = useSelector(hasApplePayPayment);

  const { mutateAsync: deletePaymentCard } = useDeletePaymentCard(() => {
    props?.onClose?.();
  });

  const { mutateAsync: updateDefaultPaymentMethod } = useUpdateDefaultPaymentMethod();

  const { t } = useTranslation();
  const { creditCardId } = useSelector(selectCurrentOrder);
  const { data } = usePaymentCards();
  const { mutateAsync: addPaymentCard, status } = useAddPaymentCard({
    onSuccess: () => {
      toast.dark(t('common.addPaymentCardSucceeded'));
      setIsAddPaymentSheetOpen(false);
    },
    onError: () => {
      toast.dark(t('common.addPaymentCardErrors'));
    },
  });

  const selectedCard = useMemo(() => data?.cards?.find((c) => c.id === creditCardId), [creditCardId, data?.cards]);

  React.useImperativeHandle(ref, () => ({
    toggleSelectPaymentMethodSheet: () => setIsSelectPaymentOpen(!isSelectPaymentOpen),
  }));

  const handleAddPaymentCard = async (card: Cards.CardForm) => {
    await addPaymentCard(card);
  };

  const paymentMethodChangedToAnalytics = (paymentMethod: Cards.PaymentMethods) => {
    Avo.paymentMethodSelected({ paymentMethod });
  };

  const handleApplePaySelected = () => {
    dispatch(confirmApplePayMethod());
    updateDefaultPaymentMethod({ method: 'APPLE_PAY' });
    paymentMethodChangedToAnalytics('APPLE_PAY');
  };

  // choose what to do once a payment mode is selected
  const handlePaymentMethodSelected = (paymentType: 'card' | 'applePay') => {
    switch (paymentType) {
      case 'card':
        setIsAddPaymentSheetOpen(true);
        break;
      case 'applePay':
        handleApplePaySelected();
        break;
      default:
        return;
    }
    setIsSelectPaymentOpen(false);
  };

  if (!data?.cards) return null;

  const handleCardSelected = (cardId: string) => {
    dispatch(setSelectedPaymentCard(cardId));
    updateDefaultPaymentMethod({ method: 'CARD', id: cardId });
    paymentMethodChangedToAnalytics('CARD');
  };

  const addPaymentHandler = () => {
    props.onClose?.();
    setIsSelectPaymentOpen(true);
  };

  const handleDeleteCard = async (cardId: string) => {
    const confirmed = await getConfirmation({
      title: t('common.deleteCardQuestion'),
    });
    if (confirmed) {
      await deletePaymentCard(cardId);
    }
  };

  return (
    <>
      <BottomSheet {...props} title={t('newOrderPage.paymentMethodQuestion')} showDone onSubmit={props.onClose}>
        <Row>
          <Col xs={12}>
            <Form>
              <ListGroup variant="flush">
                {checkApplyPayCompatibility() && (
                  <CardItem card={{ id: 'applePay', type: 'ApplePay' }} isSelected={isApplePay} handleCardSelected={handleApplePaySelected} />
                )}
                {data &&
                  data?.cards.map((card) => (
                    <CardItem
                      key={card.id}
                      card={card}
                      isSelected={selectedCard?.id === card.id}
                      handleCardSelected={handleCardSelected}
                      handleDeleteCard={handleDeleteCard}
                    />
                  ))}
              </ListGroup>
            </Form>
          </Col>
          <Col xs={12}>
            <Button variant="link" className="d-flex align-items-center mt-2 mb-2" onClick={addPaymentHandler}>
              <AddCardIcon />
              <h4 className="mb-0 margin-start-2">{t('common.addPaymentCard')}</h4>
            </Button>
          </Col>
        </Row>
      </BottomSheet>
      <SelectPaymentMethodSheet
        show={isSelectPaymentOpen}
        onClose={() => setIsSelectPaymentOpen(false)}
        onPaymentSelected={handlePaymentMethodSelected}
      />
      <AddPaymentCardSheet
        show={isAddPaymentSheetOpen}
        isAddCardButtonDisabled={status === 'loading'}
        onClose={() => setIsAddPaymentSheetOpen(false)}
        onSubmit={handleAddPaymentCard}
      />
    </>
  );
});

export default SelectPaymentSheet;
